import * as React from 'react';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { TFunction } from 'i18next';

import styled from 'styled-components';

import { PATHS } from 'router/config';
import { CoursesType } from 'components/types';
import { CoursesTableComponent, IColumn, IRow } from './CoursesTable';

export interface ICoursesTable {
  courses: CoursesType[];
  loading?: boolean;
  error?: string;
  hasMore: boolean;
  loadMore: () => void;
}

const getColumnsSource = (
  t: TFunction,
  openCourse: (id: string) => void,
): IColumn[] => [
  {
    Header: `${t('table.courses.courseName')}`,
    accessor: 'name',
    flexible: true,
    // ТЗ 0040: до этой ссылки у курса не было карточки вовсе — таблица из
    // трёх колонок и никакого перехода. Ссылка на имени, а не клик по всей
    // строке: строка ничего больше не делает, и «куда я попаду» должно быть
    // видно до нажатия.
    Cell: ({ row }: { row: { original: CoursesType } }) => (
      <CourseLink
        type="button"
        onClick={() => openCourse(String(row.original.id))}
      >
        {row.original.name}
      </CourseLink>
    ),
  },
  {
    Header: `${t('table.courses.educationForm')}`,
    accessor: 'educationForm',
    columnWidth: '160px',
    marginRight: '80px',
    marginLeft: '80px',
  },
  {
    Header: `${t('table.courses.category')}`,
    accessor: 'categoryName',
    flexible: true,
    marginRight: '120px',
  },
];

export const CoursesTable = memo(
  ({ courses, loading, error, hasMore, loadMore }: ICoursesTable) => {
    const { t } = useTranslation();
    const history = useHistory();

    const openCourse = useCallback(
      (courseId: string) => {
        history.push(PATHS.settingsCourseDetail.replace(':courseId', courseId));
      },
      [history],
    );

    const columns: IColumn[] = useMemo(() => getColumnsSource(t, openCourse), [
      t,
      openCourse,
    ]);
    const rows: IRow[] = useMemo(() => courses, [courses]);

    if (error) {
      return <p>{error}</p>;
    }

    return (
      <CoursesTableComponent
        rows={rows}
        columns={columns}
        isFlexible
        noDataText={t('table.courses.noData')}
        loadMore={loadMore}
        hasMore={hasMore}
        loading={loading}
      />
    );
  },
);

const CourseLink = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: var(--k-fg-0d53fc);
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;
